import { Sensor, useHVACData } from 'common';
import { useTranslation } from 'react-i18next';
import { Button, Text, XStack, YStack } from 'tamagui';

import { getAlertProps } from './getHVACAlertProps';
import { LoadingOverlay, TripleGlowBackground } from '../../../components/common';
import { useHVACFilterAlert } from '../../../hooks';

interface HVACFilterAlertContentProps {
  sensor?: Sensor;
  correctedStatus?: string;
}

export const HVACFilterAlertContent = ({
  sensor,
  correctedStatus,
}: HVACFilterAlertContentProps) => {
  const { t } = useTranslation();
  const { loading } = useHVACData(sensor);
  const {
    filterReplacementStatus: originalStatus,
    serviceStatus,
    onRequestService,
    onOrderFilter,
    onClearAlert,
  } = useHVACFilterAlert(sensor);

  // Determine the display status: prioritize service status, then corrected status, then original
  const filterReplacementStatus = serviceStatus || correctedStatus || originalStatus;

  const { title, icon, backgroundColor, textColor } = getAlertProps(filterReplacementStatus);

  // Override title for service states
  const getDisplayTitle = () => {
    switch (filterReplacementStatus) {
      case 'ServiceRequested':
        return t('hvac.serviceRequested');
      case 'FilterOrdered':
        return t('hvac.filterOrdered');
      default:
        return title;
    }
  };

  if (loading) {
    return <LoadingOverlay isVisible={true} />;
  }

  return (
    <YStack gap="$4" flex={1}>
      <TripleGlowBackground gradientColors={[backgroundColor]} />
      <XStack gap="$4" flex={1} alignItems="center">
        {icon}
        <Text color={textColor} fontSize="$9.5">
          {getDisplayTitle()}
        </Text>
      </XStack>

      {/* Show different content based on filterReplacementStatus */}
      {(filterReplacementStatus === 'ServiceRequested' ||
        filterReplacementStatus === 'FilterOrdered' ||
        filterReplacementStatus === 'HVACFilterAlert' ||
        filterReplacementStatus === 'HVACFilterAlertCritical' ||
        filterReplacementStatus === 'HVACFilterAlertModerate') && (
        <YStack gap="$4">
          {/* Always show the action buttons */}
          <XStack gap="$4">
            <Button
              flex={1}
              height={72}
              backgroundColor="$deltaBlue"
              borderRadius="$10"
              onPress={onRequestService}>
              <Text color="$white12" fontSize="$8.5">
                {t('hvac.requestService')}
              </Text>
            </Button>
            <Button
              flex={1}
              height={72}
              backgroundColor="$transparent"
              borderRadius="$10"
              borderWidth={2}
              borderColor="$deltaBlue"
              onPress={onClearAlert}>
              <Text color="$white12" fontSize="$8.5">
                {t('hvac.hideAlert')}
              </Text>
            </Button>
          </XStack>
          <XStack>
            <Button
              flex={1}
              height={72}
              backgroundColor="$white7"
              borderRadius="$10"
              onPress={onOrderFilter}>
              <Text color="$white12" fontSize="$8.5">
                {t('hvac.orderFilter')}
              </Text>
            </Button>
          </XStack>
        </YStack>
      )}
    </YStack>
  );
};
